import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Check, Sparkles, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PLAN_CARDS = [
  {
    tier: "basic",
    name: "Basic",
    price: "$99",
    pages: "100 pages/year",
    color: "slate",
    features: ["100 pages/yr OCR", "Email support", "TurboTax export"],
  },
  {
    tier: "premium",
    name: "Premium",
    price: "$149",
    pages: "350 pages/year",
    color: "purple",
    popular: true,
    features: ["350 pages/yr OCR", "Priority support", "Branded PDF reports", "CACFP calculators"],
  },
  {
    tier: "premium_plus",
    name: "Premium Plus",
    price: "$249",
    pages: "1,000 pages/year",
    color: "emerald",
    features: ["1,000 pages/yr OCR", "Priority phone support", "Multi-year history", "All Premium features"],
  },
];

const LEGACY_LABELS = {
  starter: "Starter (legacy)",
  pro: "Pro (legacy, 250 pages)",
  business: "Business (legacy)",
};

export default function MigrationPickerModal() {
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Only fetch once disclaimer has been accepted to avoid clashing with DisclaimerModal.
    if (!user || !user.disclaimer_accepted_at) return;
    axios.get(`${API}/migration/status`, { withCredentials: true })
      .then(res => {
        setStatus(res.data);
        setOpen(!!res.data?.needs_picker);
      })
      .catch(() => {});
  }, [user]);

  if (!open || !status || !status.needs_picker) return null;

  const currentTier = status.current_plan_tier;
  const legacyLabel = LEGACY_LABELS[status.legacy_tier_at_migration] || status.legacy_tier_at_migration;

  const keepCurrent = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/migration/acknowledge`, {}, { withCredentials: true });
      if (refreshUser) await refreshUser();
      setOpen(false);
    } catch {
      // Still close — they've seen it
      setOpen(false);
    } finally { setBusy(false); }
  };

  const switchTo = async (tier) => {
    setBusy(true);
    try {
      // Mark migration acknowledged first so the modal doesn't reappear if Square redirect fails.
      await axios.post(`${API}/migration/acknowledge`, {}, { withCredentials: true }).catch(() => {});
      const res = await axios.get(`${API}/subscriptions/checkout-url?tier=${tier}`, { withCredentials: true });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      // Fallback: navigate to pricing page in-app
      window.location.href = "/app/pricing";
    } catch {
      window.location.href = "/app/pricing";
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4 overflow-y-auto" data-testid="migration-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full my-8 relative">
        <button
          onClick={keepCurrent}
          data-testid="migration-close-btn"
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 transition-colors"
          title="Keep my current plan"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-7 border-b border-slate-100 bg-gradient-to-br from-purple-50 to-blue-50 rounded-t-2xl">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
                Welcome back — we updated our plans
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                You were on the <strong>{legacyLabel}</strong> plan. We've automatically moved you to{" "}
                <strong className="text-purple-700">{currentTier?.replace("_", " ")}</strong> ({status.current_quota} pages/year) at no extra charge.
                You can stay on this plan or switch — totally up to you.
              </p>
            </div>
          </div>
        </div>

        <div className="p-7">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {PLAN_CARDS.map(plan => {
              const isCurrent = plan.tier === currentTier;
              return (
                <div
                  key={plan.tier}
                  data-testid={`migration-plan-card-${plan.tier}`}
                  className={`relative rounded-xl p-5 border-2 transition-all ${
                    isCurrent
                      ? "border-purple-500 bg-purple-50/50 shadow-md"
                      : "border-slate-200 bg-white hover:border-slate-400"
                  }`}
                >
                  {plan.popular && !isCurrent && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                      Most Popular
                    </span>
                  )}
                  {isCurrent && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full">
                      Your Plan
                    </span>
                  )}
                  <h3 className="font-extrabold text-slate-900 text-lg" style={{ fontFamily: "Manrope" }}>{plan.name}</h3>
                  <div className="mt-2 flex items-baseline gap-1">
                    <span className="text-3xl font-extrabold text-slate-900">{plan.price}</span>
                    <span className="text-xs text-slate-500">/year</span>
                  </div>
                  <p className="text-xs font-semibold text-slate-600 mt-0.5">{plan.pages}</p>
                  <ul className="mt-4 space-y-1.5">
                    {plan.features.map(f => (
                      <li key={f} className="text-xs text-slate-600 flex items-start gap-1.5">
                        <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  {isCurrent ? (
                    <button
                      onClick={keepCurrent}
                      disabled={busy}
                      data-testid={`migration-keep-${plan.tier}`}
                      className="mt-4 w-full bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white text-sm font-bold py-2 rounded-lg transition-colors"
                    >
                      Keep this plan
                    </button>
                  ) : (
                    <button
                      onClick={() => switchTo(plan.tier)}
                      disabled={busy}
                      data-testid={`migration-switch-${plan.tier}`}
                      className="mt-4 w-full bg-white border-2 border-slate-300 hover:border-slate-500 text-slate-700 text-sm font-bold py-2 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Switch to {plan.name}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <p className="text-xs text-slate-400 italic mt-6 text-center leading-relaxed">
            Switching plans will redirect you to Square checkout. You'll need to cancel your old subscription manually from your Square email receipt.
            Add-on page packs you've purchased never expire and roll over with you to any plan.
          </p>
        </div>
      </div>
    </div>
  );
}
